'use client'

import * as React from 'react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { AuthError } from '@supabase/supabase-js'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertCircle, ArrowRight, Check, WifiOff } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

import { Button } from '@luckagents/ui/primitives/button'
import { Input } from '@luckagents/ui/primitives/text-field'
import { Label } from '@luckagents/ui/primitives/label'
import { PasswordInput } from '@/components/ui/password-input'
import { toast } from '@/lib/toast'

import { GoogleIcon } from '@/components/icons/google'
import {
  DashboardExchangeError,
  exchangeDashboardSession,
  isRateLimitedExchangeError,
  login,
} from '@/lib/auth'
import {
  redirectToDashboard,
  redirectToStripeIfNeeded,
  safeSupabaseSignOut,
} from '@/lib/auth-helpers'
import { logger } from '@/lib/logger'
import { getSupabaseClient } from '@/lib/supabase'
import { withoutTrailingSlash } from '@/lib/utils'
import { extractPlanHintFromLocation } from '@/lib/plan'
import {
  extractBillingPeriodFromLocation,
  normalizeBillingPeriod,
  type BillingPeriod,
} from '@/lib/billing'
import { formatRateLimitMessage, useRateLimitCooldown } from '@/hooks/use-rate-limit'
import { consumeRateLimitNotice } from '@/lib/rate-limit-notice'

/* ------------------------------------------------------------------ */
/*  Shared input class                                                 */
/* ------------------------------------------------------------------ */
const AUTH_INPUT_CLASS =
  'h-12 rounded-xl bg-zinc-50 px-4 text-[15px] transition-all duration-200 border border-zinc-200 focus:bg-white focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/5 dark:bg-zinc-900/50 dark:border-zinc-800 dark:focus:bg-zinc-900 dark:focus:border-zinc-600 dark:focus:ring-white/5 placeholder:text-zinc-400 dark:placeholder:text-zinc-500'

const AUTH_INPUT_ERROR = 'border-red-400 focus:border-red-500 focus:ring-red-500/10'

const AUTH_EASE: [number, number, number, number] = [0.25, 0.4, 0.55, 1]

/* ------------------------------------------------------------------ */
/*  Stagger animation helpers                                          */
/* ------------------------------------------------------------------ */
const stagger = {
  container: {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.04 },
    },
  },
  item: {
    hidden: { opacity: 0, y: 8 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: AUTH_EASE },
    },
  },
}

/* ------------------------------------------------------------------ */
/*  Schema                                                             */
/* ------------------------------------------------------------------ */
const loginSchema = z.object({
  email: z.string().email('Ingresa un correo válido'),
  password: z.string().min(1, 'Ingresa tu contraseña'),
})

type LoginFormData = z.infer<typeof loginSchema>

type LoginFormCardProps = {
  planHint?: string | null
  billingPeriodHint?: BillingPeriod | string | null
  redirectToCheckout?: boolean
  oauthRedirectPath?: string
}

export function LoginFormCard({
  planHint: planHintProp,
  billingPeriodHint: billingPeriodHintProp,
  redirectToCheckout = false,
  oauthRedirectPath,
}: LoginFormCardProps = {}) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [ssoLoading, setSsoLoading] = React.useState(false)
  const [formError, setFormError] = React.useState('')
  const [submitSuccess, setSubmitSuccess] = React.useState(false)
  const { cooldownSeconds, isCoolingDown, startCooldown } = useRateLimitCooldown()
  const supabase = React.useMemo(() => getSupabaseClient(), [])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  React.useEffect(() => {
    const notice = consumeRateLimitNotice()
    if (!notice) return
    const remaining = notice.until - Date.now()
    if (remaining > 0) {
      startCooldown(remaining)
    }
  }, [startCooldown])

  const forceSupabaseLogout = React.useCallback(async () => {
    await safeSupabaseSignOut(supabase)
  }, [supabase])

  /* Determine error type for refined messaging */
  const isNetworkError = formError.includes('conexión') || formError.includes('conectar')

  const onSubmit = async (data: LoginFormData) => {
    setFormError('')
    if (isCoolingDown) return

    try {
      const session = await login({ email: data.email, password: data.password })

      const billingPeriod = normalizeBillingPeriod(
        billingPeriodHintProp ?? extractBillingPeriodFromLocation()
      )
      let exchangeResult
      try {
        exchangeResult = await exchangeDashboardSession(session, {
          plan: planHintProp ?? null,
          billingPeriod,
        })
      } catch (error) {
        if (isRateLimitedExchangeError(error)) {
          startCooldown(error.retryAfterMs)
          return
        }
        await forceSupabaseLogout()
        if (error instanceof DashboardExchangeError) {
          logger.error('[Auth] No fue posible sincronizar la sesión con el dashboard', {
            code: error.code,
            error,
          })
        } else {
          logger.error('[Auth] Error inesperado al sincronizar la sesión con el dashboard', {
            error,
          })
        }
        setFormError('No pudimos sincronizar tu sesión con el dashboard. Inténtalo nuevamente.')
        return
      }

      if (!exchangeResult) {
        await forceSupabaseLogout()
        setFormError('No pudimos sincronizar tu sesión con el dashboard. Inténtalo nuevamente.')
        return
      }

      /* Success flash before redirect */
      setSubmitSuccess(true)

      const redirectMessage = redirectToCheckout
        ? 'Redirigiendo a Stripe para completar el pago…'
        : 'Redirigiendo para continuar la configuración…'
      toast.success('Sesión iniciada', {
        description: redirectMessage,
      })

      if (redirectToStripeIfNeeded(exchangeResult, { enabled: redirectToCheckout })) {
        return
      }

      redirectToDashboard((path) => router.push(path), searchParams.get('next'))
    } catch (error) {
      logger.error('[Login] Error during login attempt', { error })
      if (error instanceof AuthError) {
        const message = error.message?.toLowerCase() ?? ''
        if (error.status === 429 || message.includes('rate')) {
          startCooldown()
        } else if (message.includes('network')) {
          setFormError(
            'No pudimos conectar con el servidor. Revisa tu conexión e inténtalo nuevamente.'
          )
        } else {
          setFormError('Correo o contraseña inválidos. Intenta de nuevo.')
        }
      } else if (error instanceof Error && error.message === 'SESSION_NOT_AVAILABLE') {
        setFormError('No fue posible obtener la sesión. Intenta nuevamente.')
      } else {
        setFormError('Credenciales inválidas o error de red.')
      }
    }
  }

  async function handleSso() {
    setFormError('')
    if (isCoolingDown) return
    setSsoLoading(true)
    try {
      const portalBase =
        withoutTrailingSlash(process.env.NEXT_PUBLIC_PORTAL_URL ?? window.location.origin) ??
        window.location.origin
      const planHint = planHintProp ?? extractPlanHintFromLocation()
      const billingPeriod = normalizeBillingPeriod(
        billingPeriodHintProp ?? extractBillingPeriodFromLocation()
      )
      const params = new URLSearchParams()
      if (planHint) {
        params.set('plan', planHint)
      }
      if (billingPeriod) {
        params.set('period', billingPeriod)
      }
      const redirectPath =
        oauthRedirectPath ?? (params.toString() ? `/access?${params.toString()}` : '/access')

      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${portalBase}${redirectPath}`,
        },
      })
    } catch (error) {
      logger.error('[Auth] No pudimos abrir el acceso con Google', { error })
      setFormError('No pudimos abrir el acceso con Google. Intenta nuevamente.')
      setSsoLoading(false)
    }
  }

  const isDisabled = isSubmitting || ssoLoading || isCoolingDown

  return (
    <motion.div className="space-y-6" variants={stagger.container} initial="hidden" animate="show">
      {/* ---------- Google SSO (primary action) ---------- */}
      <motion.div variants={stagger.item}>
        <button
          type="button"
          onClick={() => void handleSso()}
          disabled={isDisabled}
          className="group relative flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 text-[15px] font-medium text-zinc-800 shadow-sm transition-all duration-200 hover:translate-y-[-1px] hover:border-zinc-300 hover:bg-zinc-50 hover:shadow-md active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-zinc-600 dark:hover:bg-zinc-800"
        >
          <GoogleIcon className="h-[18px] w-[18px] shrink-0" />
          <span>Continuar con Google</span>
        </button>
      </motion.div>

      {/* ---------- Divider ---------- */}
      <motion.div variants={stagger.item} className="relative py-1">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.3, duration: 0.5, ease: AUTH_EASE }}
            className="w-full origin-center border-t border-zinc-200/60 dark:border-zinc-800/60"
          />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-3 text-[11px] font-medium uppercase tracking-wide text-zinc-400 dark:bg-zinc-950 dark:text-zinc-500">
            o con email
          </span>
        </div>
      </motion.div>

      {/* ---------- Form ---------- */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4"
        noValidate
        aria-label="Iniciar sesión"
      >
        {/* Email */}
        <motion.div variants={stagger.item} className="space-y-1">
          <Label htmlFor="email" className="sr-only">
            Correo
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            autoFocus
            spellCheck={false}
            className={`${AUTH_INPUT_CLASS} ${errors.email ? AUTH_INPUT_ERROR : ''}`}
            placeholder="nombre@empresa.com"
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'login-email-error' : undefined}
            {...register('email')}
          />
          <AnimatePresence mode="wait">
            {errors.email && (
              <motion.p
                id="login-email-error"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="text-xs font-medium text-red-500"
                role="status"
                aria-live="polite"
              >
                {errors.email.message}
              </motion.p>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Password */}
        <motion.div variants={stagger.item} className="space-y-1">
          <Label htmlFor="password" className="sr-only">
            Contraseña
          </Label>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            className={`${AUTH_INPUT_CLASS} ${errors.password ? AUTH_INPUT_ERROR : ''}`}
            placeholder="Contraseña"
            aria-invalid={Boolean(errors.password)}
            aria-describedby={errors.password ? 'login-password-error' : undefined}
            {...register('password')}
          />
          <div className="flex justify-end pt-0.5">
            <Link
              href="/forgot-password"
              prefetch
              className="text-[12px] font-medium text-zinc-400 transition-colors hover:text-zinc-700 dark:text-zinc-500 dark:hover:text-zinc-300"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
          <AnimatePresence mode="wait">
            {errors.password && (
              <motion.p
                id="login-password-error"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="text-xs font-medium text-red-500"
                role="status"
                aria-live="polite"
              >
                {errors.password.message}
              </motion.p>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Error alert — refined */}
        <AnimatePresence mode="wait">
          {(formError || isCoolingDown) && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className={`flex items-start gap-3 rounded-xl border p-3.5 text-[13px] leading-relaxed ${
                isNetworkError
                  ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-400'
                  : 'border-red-200 bg-red-50 text-red-600 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-400'
              }`}
              role="alert"
              aria-live="polite"
            >
              {isNetworkError ? (
                <WifiOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              ) : (
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              )}
              <span>{isCoolingDown ? formatRateLimitMessage(cooldownSeconds) : formError}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA Button */}
        <motion.div variants={stagger.item} className="pt-1">
          <Button
            type="submit"
            className={`group h-12 w-full rounded-xl text-[15px] font-semibold shadow-sm transition-all duration-200 active:scale-[0.98] ${
              submitSuccess
                ? 'bg-emerald-600 text-white hover:bg-emerald-600 dark:bg-emerald-500 dark:text-white'
                : 'bg-zinc-900 text-white hover:bg-zinc-800 hover:shadow-md dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100'
            }`}
            disabled={isDisabled || submitSuccess}
            aria-busy={isSubmitting}
          >
            {submitSuccess ? (
              <span className="flex items-center gap-2">
                <Check className="h-5 w-5" aria-hidden="true" />
                Listo
              </span>
            ) : isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg
                  className="h-4 w-4 motion-safe:animate-spin"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Ingresando...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Continuar
                <ArrowRight
                  className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </span>
            )}
          </Button>
        </motion.div>

        {/* Switch to register */}
        <motion.div variants={stagger.item} className="pt-2 text-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            ¿No tienes cuenta?{' '}
            <Link
              href="/register"
              className="font-medium text-zinc-900 underline-offset-2 transition-colors hover:underline dark:text-white"
            >
              Regístrate gratis
            </Link>
          </p>
        </motion.div>
      </form>
    </motion.div>
  )
}
